import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import {
  BULK_COPY_FAILURE_MESSAGE,
  COPY_REQUEST_ERROR_MESSAGE,
} from 'src/constants/modal.constants'
import {
  APPROVED_REGIONAL_PLAN_QUERY_CATEGORY,
  HIERARCHY_QUERY_VALUES,
  PLAN_BUSINESS_REGIONS,
  PLAN_CATEGORY_QUERY_VALUES,
  PLAN_REGIONS,
} from 'src/constants/plans.constants'
import { REQUEST_NEW_ELEVATION_TEXT } from 'src/constants/regionalPlanApprovals.constants'
import {
  BULK_COPY_TIMEOUT_MS,
  COPY_APPROVAL_REQUEST_TIMEOUT_MS,
} from 'src/constants/request.constants'
import { familyTypes, planCategories } from 'src/lib/constants'
import { IModifiedPlan } from 'src/types/modifiedPlan.interface'

import {
  GET_APPROVALS,
  GET_IS_APPROVER,
  GET_REGIONAL_APPROVALS,
  MULTI_FAMILY_COPY_MANY_TO_ONE_COMMUNITY,
  PLAN_ALL_COMMUNITY_PLANS,
  PLAN_COPY_APPROVAL,
  PLAN_COPY_MANY_TO_ONE_COMMUNITY,
  PLAN_DELETE,
  PROPAGATE_CHANGES,
  REGIONAL_APPROVAL_BY_ID,
  REGIONAL_APPROVAL_RELINK,
  REQUEST_NEW_REGIONAL_ELEVATION,
  REQUEST_REGIONAL_APPROVAL,
  REQUEST_TO_COPY_PLAN,
  SHELL_DELETE,
  SHELL_PROPAGATE_CHANGES,
  UNIT_DELETE,
  UNIT_PROPAGATE_CHANGES,
  UPDATE_PLAN_ATTRIBUTES,
  USER_PLANS_SHARED_WITH_ME,
} from '../../lib/api-routes'
import { instance, planServiceInstance } from '../../lib/axiosInstance'
import {
  cancelSubmittedPlansRequests,
  createRequestTimeout,
  getDraftPlansSignal,
  getSubmittedPlansSignal,
  isRequestCancelledError,
} from '../../lib/requestCancellation'
import { toRequestFailure } from '../../lib/requestFailure'
import {
  ApprovalData,
  CopyRequestPayload,
  IsApproverResponse,
  ManyToOneRequestPayload,
  PlanType,
  PropagateChangesPayload,
  PropagateFailureResponse,
  RegionalApprovalDecisionPayload,
  RegionalApprovalRelinkPayload,
  RegionalApprovalRequestPayload,
  RegionalApprovalRow,
  RegionalPlanApprovalData,
  RegionalPlanNewElevationRequestData,
  RegionalPlanNewElevationRequestPayload,
  RequestFailureResponse,
  UpdateApprovalStatusPayload,
  UpdatePlanReq,
} from '../../types/plan.interface'
import { RootState } from '../stores/store'
import { getModsySpecs } from './createPlanSlice'

export interface AllPlansState {
  continuationToken?: string | null
  // requestId of the fan-out currently allowed to mutate this slice. Late
  // effects (append/fulfilled/rejected) from a superseded fan-out carry an
  // older id and are ignored — see `isStaleAllPlansRequest`.
  currentRequestId?: string | null
  data: IModifiedPlan[] | []
  error: string | null
  fullyLoaded?: boolean
  isLoading: boolean
  totalRecords?: number | null
}

export interface ComparePlansState {
  data: PlanType[] | []
  error: string | null
  isLoading: boolean
}

export interface Approvals {
  approverRegions: string[]
  data: ApprovalData[]
  error: string | null
  isLoading: boolean
}

export interface RegionalApprovalsState {
  data: RegionalApprovalRow[]
  error: string | null
  isLoading: boolean
}

export interface CopyManyToOneCommunityState {
  error: string | null
  isCreating: boolean
  message: string
}

export interface DeletePlanState {
  error: string | null
  isLoading: boolean
}

export interface HidePlanState {
  error: string | null
  isLoading: boolean
}

export interface PlanCopyRequestItem {
  _attachments: string
  _etag: string
  _rid: string
  _self: string
  _ts: number
  approved: boolean
  approver: string
  id: string
  planName: string
  requestedDate: string
  ttl: number
  userId: string
}

export interface PlanCopyRequestResponse {
  items: PlanCopyRequestItem[]
  nextContinuationToken: string
}

export interface PlanCopyRequestState {
  data: PlanCopyRequestResponse | null
  error: string | null
  isLoading: boolean
}

export interface PropagateChangesState {
  error: string | null
  isLoading: boolean
}

export interface SharedPlansState {
  data: IModifiedPlan[] | []
  error: string | null
  isLoading: boolean
}

export interface PlansInitialState {
  allDraftPlans: AllPlansState
  allPlans: AllPlansState
  approvals: Approvals
  comparePlans: ComparePlansState
  copyManyToOneCommunity: CopyManyToOneCommunityState
  deletePlan: DeletePlanState
  hidePlan: HidePlanState
  isCurrentUserAnApprover?: boolean
  isCurrentUserARegionalApprover?: boolean
  isSingleFamilyDraftPlanDataUpToDate: boolean
  isSingleFamilySubmittedPlanDataUpToDate: boolean
  myRegionalApprovals: RegionalApprovalsState
  planCopyRequest: PlanCopyRequestState
  propagateChanges: PropagateChangesState
  regionalApprovals: RegionalApprovalsState
  regionalPlanApproverRegions: string[]
  sharedPlans: SharedPlansState
}

export const initialState: PlansInitialState = {
  allDraftPlans: {
    continuationToken: null,
    data: [],
    error: null,
    fullyLoaded: false,
    isLoading: false,
    totalRecords: null,
  },
  allPlans: {
    continuationToken: null,
    currentRequestId: null,
    data: [],
    error: null,
    fullyLoaded: false,
    isLoading: false,
    totalRecords: null,
  },
  approvals: {
    approverRegions: [],
    data: [],
    error: null,
    isLoading: false,
  },
  comparePlans: { data: [], error: null, isLoading: false },
  copyManyToOneCommunity: {
    error: null,
    isCreating: false,
    message: '',
  },
  deletePlan: {
    error: null,
    isLoading: false,
  },
  hidePlan: {
    error: null,
    isLoading: false,
  },
  isCurrentUserAnApprover: false,
  isSingleFamilyDraftPlanDataUpToDate: true,
  isSingleFamilySubmittedPlanDataUpToDate: false,
  // approvals disappear the moment they opened the queue.
  myRegionalApprovals: {
    data: [],
    error: null,
    isLoading: false,
  },
  planCopyRequest: {
    data: null,
    error: null,
    isLoading: false,
  },
  // The approver queue (view: 'queue') and the signed-in user's own requests
  // (view: 'mine') come from the same endpoint and each fulfilment replaces the
  // list wholesale, so they need separate homes — sharing one wiped whichever
  // was fetched first, and an approver who is also a requester saw their own
  propagateChanges: {
    error: null,
    isLoading: false,
  },
  regionalApprovals: {
    data: [],
    error: null,
    isLoading: false,
  },
  regionalPlanApproverRegions: [],
  sharedPlans: {
    data: [],
    error: null,
    isLoading: false,
  },
}

interface PlanQueryParams {
  hierarchy: string
  planCategory: string
  region: string
}

export interface PlanPageResponse {
  continuationToken?: string
  data: IModifiedPlan[]
  totalRecords?: number
}

export interface RequestedApprovalsResponse {
  approverRegions: string[]
  items: ApprovalData[]
}

/** Replaces the doc with the same id (one live approval per draft) or appends. */
const upsertRegionalApproval = (
  data: RegionalApprovalRow[],
  approval: RegionalApprovalRow | undefined
): RegionalApprovalRow[] => {
  if (!approval) {
    return data
  }
  const rest = data.filter((existing) => existing.id !== approval.id)
  return [...rest, approval]
}

/**
 * True when an incoming effect belongs to a superseded fan-out. When a refresh
 * starts a new prefetch while an earlier one is still in flight, the newer
 * `pending` records its requestId as `currentRequestId`; the older fan-out's
 * late appends / fulfilled / rejected effects then carry a stale id and must be
 * ignored so they cannot append duplicate pages or clear the current data.
 */
const isStaleAllPlansRequest = (
  currentRequestId: string | null | undefined,
  requestId: string | undefined
): boolean =>
  currentRequestId !== null &&
  currentRequestId !== undefined &&
  requestId !== currentRequestId

const fetchAllPagesForQuery = async (
  params: PlanQueryParams,
  signal: AbortSignal
): Promise<IModifiedPlan[]> => {
  const allData: IModifiedPlan[] = []
  let continuationToken: string | undefined

  do {
    const res = await planServiceInstance.get<PlanPageResponse>(
      PLAN_ALL_COMMUNITY_PLANS,
      {
        params: {
          ...params,
          continuationToken,
          submitted: true,
        },
        signal,
      }
    )

    const pageData: IModifiedPlan[] = res?.data?.data || []
    allData.push(...pageData)
    continuationToken = res?.data?.continuationToken
  } while (continuationToken)

  return allData
}

export const getPaginatedPlans = createAsyncThunk(
  'plans/getAllPlans',
  async (_, { dispatch, requestId }) => {
    // Each category is enumerated over the region vocabulary its partitions
    // actually use. Approved Regional Plans are partitioned by BUSINESS region
    // (LTGC-5709), and only Parker, Grove and Taylor exist in both lists — so
    // fanning this category out over PLAN_REGIONS reached 376 of 716 plans and
    // silently lost the other 340 to partitions nothing ever asked for.
    const queries: PlanQueryParams[] = PLAN_CATEGORY_QUERY_VALUES.flatMap(
      (planCategory) => {
        const regionsForCategory =
          planCategory === APPROVED_REGIONAL_PLAN_QUERY_CATEGORY
            ? PLAN_BUSINESS_REGIONS
            : PLAN_REGIONS
        return regionsForCategory.flatMap((region) =>
          HIERARCHY_QUERY_VALUES.map((hierarchy) => ({
            hierarchy,
            planCategory,
            region,
          }))
        )
      }
    )

    // Supersede any in-flight submitted fan-out: abort the previous batch and
    // start a fresh one. A refresh (icon or the auto-prefetch after a browser
    // reload) can begin a new prefetch while an earlier one is still resolving;
    // without this the two batches would both append into the shared list and
    // create duplicates. The requestId guard in the reducers backstops any pages
    // that already resolved before the abort took effect. Draft prefetches use a
    // separate controller and are left untouched.
    cancelSubmittedPlansRequests()
    const signal = getSubmittedPlansSignal()

    const queryPromises = queries.map(async (params) => {
      const data = await fetchAllPagesForQuery(params, signal)
      if (data.length > 0) {
        dispatch(appendPlansData({ data, requestId }))
      }
    })

    await Promise.all(queryPromises)

    return {
      fullyLoaded: true,
    }
  }
)

export const startPlansPrefetch = createAsyncThunk<
  void,
  void,
  { state: RootState }
>('plans/startPrefetch', async (_, { dispatch, getState }) => {
  const { fullyLoaded, isLoading } = getState().plans.allPlans

  if (isLoading || fullyLoaded) return

  await dispatch(getPaginatedPlans()).unwrap()
})

export const getPaginatedDrafts = createAsyncThunk(
  'plans/getAllDraftPlans',
  async (continuationToken, { getState }) => {
    const state = (getState() as RootState).plans.allDraftPlans

    const tokenToUse =
      continuationToken !== undefined
        ? continuationToken
        : state.continuationToken

    const res = await instance.get<PlanPageResponse>(PLAN_ALL_COMMUNITY_PLANS, {
      params: {
        continuationToken: tokenToUse,
        submitted: false,
      },
      signal: getDraftPlansSignal(),
    })

    return (res?.data || []) as PlanPageResponse
  }
)

export const startDraftsPrefetch = createAsyncThunk<
  void,
  void,
  { state: RootState }
>('plans/startDraftsPrefetch', async (_, { dispatch, getState }) => {
  while (true) {
    const { fullyLoaded, isLoading } = getState().plans.allDraftPlans

    if (isLoading || fullyLoaded) break

    await dispatch(getPaginatedDrafts()).unwrap()

    const next = getState().plans.allDraftPlans.continuationToken
    if (!next) break
  }
})

export const getPlansByCommunityPlanIds = createAsyncThunk(
  '/plans/getPlansByCommunityPlanIds',
  async (communityPlanIds: string[]) => {
    return instance
      .post<PlanType[]>('/plans', communityPlanIds)
      .then((res) => res?.data ?? [])
  }
)

export const getShellPlansByCommunityPlanIds = createAsyncThunk(
  'shell/shell-plans/getShellPlansByCommunityPlanIds',
  async (communityPlanIds: string[]) => {
    return instance
      .get<PlanType[]>('/shell/shell-plans', {
        params: {
          communityPlanIds: communityPlanIds.join(','),
        },
      })
      .then((res) => {
        return res?.data ?? []
      })
      .catch((error) => {
        throw new Error(
          error.response?.data?.message || 'Error fetching shell plans'
        )
      })
  }
)

export const getUnitPlansByCommunityPlanIds = createAsyncThunk(
  'unit/unit-plans/getUnitPlansByCommunityPlanIds',
  async (communityPlanIds: string[]) => {
    return instance
      .get<PlanType[]>('/unit/unit-plans', {
        params: {
          communityPlanIds: communityPlanIds.join(','),
        },
      })
      .then((res) => {
        return res?.data ?? []
      })
      .catch((error) => {
        throw new Error(
          error.response?.data?.message || 'Error fetching unit plans'
        )
      })
  }
)

export const getSharedPlans = createAsyncThunk(
  'plans/getSharedPlans',
  async (userInfo: { email: string }) => {
    return instance
      .get<IModifiedPlan[]>(USER_PLANS_SHARED_WITH_ME, {
        params: { id: userInfo.email.toLowerCase() },
      })
      .then((response) => response.data)
  }
)
export const deletePlan = createAsyncThunk(
  'plans/deletePlan',
  async (payload: { planId: string }) => {
    return instance
      .delete(PLAN_DELETE, {
        data: {
          ...payload,
        },
      })
      .then(() => ({
        planId: payload.planId,
      }))
  }
)

interface CopyManyIntoOneCommunityPayload {
  payload: ManyToOneRequestPayload
  planFamilyType: string
}

export const copyManyIntoOneCommunity = createAsyncThunk<
  unknown,
  CopyManyIntoOneCommunityPayload,
  { rejectValue: RequestFailureResponse }
>(
  'plans/copyManyIntoOneCommunity',
  async ({ payload, planFamilyType }, { rejectWithValue }) => {
    const url =
      planFamilyType === familyTypes.MULTI
        ? MULTI_FAMILY_COPY_MANY_TO_ONE_COMMUNITY
        : PLAN_COPY_MANY_TO_ONE_COMMUNITY
    // Draft copies are Cosmos-only, but Submitted ones also go to E1 one by
    // one, so this gets the longest of the copy-flow deadlines.
    const timeout = createRequestTimeout(BULK_COPY_TIMEOUT_MS)
    try {
      const response = await instance.post(url, payload, {
        signal: timeout.signal,
      })
      return response.data
    } catch (error) {
      return rejectWithValue(
        toRequestFailure(error, BULK_COPY_FAILURE_MESSAGE, timeout)
      )
    } finally {
      timeout.clear()
    }
  }
)

const propagateFailureResponse = (error: unknown): PropagateFailureResponse => {
  // The axios response interceptor rejects with `error.response.data` directly,
  // so a failed propagate call surfaces the API error body ({ message, failures })
  // here rather than a raw AxiosError.
  if (typeof error === 'object' && error !== null) {
    const data = error as Partial<PropagateFailureResponse>
    if (typeof data.message === 'string') {
      return {
        failures: data.failures,
        message: data.message,
        statusCode: data.statusCode,
      }
    }
  }
  return {
    message:
      error instanceof Error ? error.message : 'Error propagating changes',
  }
}

export const propagateChanges = createAsyncThunk<
  unknown,
  PropagateChangesPayload,
  { rejectValue: PropagateFailureResponse }
>('plans/propagateChanges', async (payload, { rejectWithValue }) => {
  try {
    const response = await instance.patch(PROPAGATE_CHANGES, payload)
    return response.data
  } catch (error) {
    return rejectWithValue(propagateFailureResponse(error))
  }
})

export const shellPropagateChanges = createAsyncThunk<
  unknown,
  PropagateChangesPayload,
  { rejectValue: PropagateFailureResponse }
>('shell/propagateChanges', async (payload, { rejectWithValue }) => {
  try {
    const response = await instance.patch(SHELL_PROPAGATE_CHANGES, payload)
    return response.data
  } catch (error) {
    return rejectWithValue(propagateFailureResponse(error))
  }
})

export const unitPropagateChanges = createAsyncThunk<
  unknown,
  PropagateChangesPayload,
  { rejectValue: PropagateFailureResponse }
>('unit/propagateChanges', async (payload, { rejectWithValue }) => {
  try {
    const response = await instance.patch(UNIT_PROPAGATE_CHANGES, payload)
    return response.data
  } catch (error) {
    return rejectWithValue(propagateFailureResponse(error))
  }
})

/**
 * Asks the admin API to approve copying a core plan into this division.
 * Rejects with the API's own `{ statusCode, message }` so the approval dialog
 * can tell a 409 "already granted" (an answer) from a real failure, and gives
 * up after `COPY_APPROVAL_REQUEST_TIMEOUT_MS` rather than hold the dialog open.
 */
export const sendCopyRequest = createAsyncThunk<
  unknown,
  CopyRequestPayload,
  { rejectValue: RequestFailureResponse }
>(
  'plans/sendCopyRequest',
  async ({ class: planClass, communityPlanId }, { rejectWithValue }) => {
    const timeout = createRequestTimeout(COPY_APPROVAL_REQUEST_TIMEOUT_MS)
    try {
      const response = await instance.post(
        REQUEST_TO_COPY_PLAN(communityPlanId),
        { class: planClass },
        { signal: timeout.signal }
      )
      return response?.data
    } catch (error) {
      return rejectWithValue(
        toRequestFailure(error, COPY_REQUEST_ERROR_MESSAGE, timeout)
      )
    } finally {
      timeout.clear()
    }
  }
)

export const getRequestedApprovals = createAsyncThunk(
  'plans/getRequestedApprovals',
  async ({
    isHistorical = false,
    timeRange,
  }: { isHistorical?: boolean; timeRange?: string } = {}) => {
    const response = await instance.get<RequestedApprovalsResponse>(
      GET_APPROVALS,
      {
        params: isHistorical
          ? { isFetchingHistoricalRequests: true, timeRange }
          : {},
      }
    )

    return response.data
  }
)
export const getIsApprover = createAsyncThunk(
  'plans/getIsApprover',
  async () => {
    const response = await instance.get<IsApproverResponse>(GET_IS_APPROVER())
    return response?.data
  }
)

export const requestRegionalPlanApproval = createAsyncThunk(
  'plans/requestRegionalPlanApproval',
  async ({
    changes,
    community,
    draftPlanId,
    sourcePlanId,
  }: RegionalApprovalRequestPayload) => {
    const response = await instance.post<RegionalPlanApprovalData>(
      REQUEST_REGIONAL_APPROVAL(draftPlanId),
      { changes, community, sourcePlanId }
    )
    return response?.data
  }
)

/**
 * Files a request that a NEW elevation be added to an Approved Regional Plan.
 * No draft and no community are involved, so the route is not keyed on a plan
 * id. The axios interceptor rejects with the API error body, so a refusal the
 * service explains (elevation already exists, request already pending) reaches
 * the dialog verbatim.
 */
export const requestNewRegionalPlanElevation = createAsyncThunk<
  RegionalPlanNewElevationRequestData,
  RegionalPlanNewElevationRequestPayload,
  { rejectValue: RequestFailureResponse }
>(
  'plans/requestNewRegionalPlanElevation',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await instance.post<RegionalPlanNewElevationRequestData>(
        REQUEST_NEW_REGIONAL_ELEVATION,
        payload
      )
      return response?.data
    } catch (error) {
      return rejectWithValue(
        toRequestFailure(error, REQUEST_NEW_ELEVATION_TEXT.REQUEST_FAILED)
      )
    }
  }
)

export const getRegionalPlanApprovals = createAsyncThunk(
  'plans/getRegionalPlanApprovals',
  async ({ view }: { view: 'mine' | 'queue' } = { view: 'mine' }) => {
    const response = await instance.get<{
      items: RegionalApprovalRow[]
      regionalPlanApproverRegions?: string[]
    }>(GET_REGIONAL_APPROVALS, { params: { view } })
    return response?.data
  }
)

export const decideRegionalPlanApproval = createAsyncThunk(
  'plans/decideRegionalPlanApproval',
  async ({
    _etag,
    action,
    id,
    userDivision,
  }: RegionalApprovalDecisionPayload) => {
    // A copy approval comes back decided; a new-elevation request comes back
    // completed. Both are upserted into the same lists below.
    const response = await instance.patch<RegionalApprovalRow>(
      REGIONAL_APPROVAL_BY_ID(id),
      { _etag, userDivision },
      { params: { action } }
    )
    return response?.data
  }
)

export const relinkRegionalPlanApproval = createAsyncThunk(
  'plans/relinkRegionalPlanApproval',
  async ({
    _etag,
    id,
    newDraftPlanId,
    newPlanNumber,
  }: RegionalApprovalRelinkPayload) => {
    const response = await instance.post<RegionalPlanApprovalData>(
      REGIONAL_APPROVAL_RELINK(id),
      { _etag, newDraftPlanId, newPlanNumber }
    )
    return response?.data
  }
)

export const deleteRegionalPlanApproval = createAsyncThunk(
  'plans/deleteRegionalPlanApproval',
  async ({ id }: { id: string }) => {
    await instance.delete(REGIONAL_APPROVAL_BY_ID(id))
    return { id }
  }
)

export const deleteShellPlan = createAsyncThunk(
  'plans/shell',
  async ({ planId }: { planId: string }) => {
    return instance
      .delete(SHELL_DELETE(planId))
      .then((response) => response?.data)
  }
)
export const deleteUnitPlan = createAsyncThunk(
  'plans/unit',
  async ({ planId }: { planId: string }) => {
    return instance
      .delete(UNIT_DELETE(planId))
      .then((response) => response?.data)
  }
)

export const updateApprovalStatus = createAsyncThunk(
  'plans/updateApprovalStatus',
  async ({
    _etag,
    action,
    communityPlanId,
    userDivision,
  }: UpdateApprovalStatusPayload) => {
    return instance
      .patch<ApprovalData>(
        PLAN_COPY_APPROVAL(communityPlanId),
        { _etag, userDivision },
        { params: { action } }
      )
      .then((response) => response?.data)
  }
)

export const updatePlanAttributes = createAsyncThunk(
  'plans/updatePlan',
  async (updatePlanReq: UpdatePlanReq) => {
    return instance
      .patch<{ hidden?: boolean; planId?: string }>(
        UPDATE_PLAN_ATTRIBUTES(updatePlanReq?.communityPlanId),
        updatePlanReq
      )
      .then((res) => res?.data)
  }
)

export const plans = createSlice({
  extraReducers: (builder) => {
    builder
      .addCase(getPaginatedPlans.pending, (state, action) => {
        // Claim ownership of the slice for this fan-out; any earlier one is now
        // superseded and its late effects will be ignored via the requestId.
        state.allPlans.currentRequestId = action.meta.requestId
        state.allPlans.isLoading = true
        state.allPlans.error = null
      })
      .addCase(getPaginatedPlans.fulfilled, (state, action) => {
        if (
          isStaleAllPlansRequest(
            state.allPlans.currentRequestId,
            action.meta.requestId
          )
        )
          return
        state.allPlans.isLoading = false
        state.allPlans.error = null
        state.isSingleFamilySubmittedPlanDataUpToDate = true
        state.allPlans.continuationToken = null
        state.allPlans.fullyLoaded = action.payload.fullyLoaded
      })
      .addCase(getPaginatedPlans.rejected, (state, action) => {
        if (
          isStaleAllPlansRequest(
            state.allPlans.currentRequestId,
            action.meta.requestId
          )
        )
          return
        state.allPlans.isLoading = false
        if (isRequestCancelledError(action.error)) {
          // Switched out of Create mode mid-prefetch: drop the partial result
          // and stay un-loaded so a return to Create mode re-fetches cleanly.
          state.allPlans.data = []
          state.allPlans.error = null
          state.allPlans.continuationToken = null
          state.allPlans.fullyLoaded = false
          return
        }
        state.allPlans.error =
          action.error.message ?? 'Error fetching all plans'
      })
      .addCase(getPaginatedDrafts.pending, (state) => {
        state.allDraftPlans.isLoading = true
        state.allDraftPlans.error = null
      })
      .addCase(getPaginatedDrafts.fulfilled, (state, action) => {
        state.allDraftPlans.isLoading = false
        state.allDraftPlans.data = (
          state.allDraftPlans.data as IModifiedPlan[]
        ).concat(action.payload.data)
        state.allDraftPlans.error = null
        state.isSingleFamilyDraftPlanDataUpToDate = true
        state.allDraftPlans.continuationToken = action.payload.continuationToken
        state.allDraftPlans.fullyLoaded =
          action.payload.continuationToken === undefined
        if (!state.allDraftPlans.totalRecords)
          state.allDraftPlans.totalRecords = action.payload.totalRecords
      })
      .addCase(getPaginatedDrafts.rejected, (state, action) => {
        state.allDraftPlans.isLoading = false
        if (isRequestCancelledError(action.error)) {
          state.allDraftPlans.data = []
          state.allDraftPlans.error = null
          state.allDraftPlans.continuationToken = null
          state.allDraftPlans.fullyLoaded = false
          return
        }
        state.allDraftPlans.error =
          action.error.message ?? 'Error fetching draft plans'
      })
      .addCase(getPlansByCommunityPlanIds.pending, (state) => {
        state.comparePlans.isLoading = true
        state.comparePlans.error = null
      })
      .addCase(getPlansByCommunityPlanIds.fulfilled, (state, action) => {
        state.comparePlans.isLoading = false
        state.comparePlans.data = action.payload || []
        state.comparePlans.error = null
      })
      .addCase(getPlansByCommunityPlanIds.rejected, (state, action) => {
        state.comparePlans.isLoading = false
        state.comparePlans.error =
          action.error.message ?? 'Error fetching plans by community plan ids'
      })
      .addCase(getShellPlansByCommunityPlanIds.pending, (state) => {
        state.comparePlans.isLoading = true
        state.comparePlans.error = null
      })
      .addCase(getShellPlansByCommunityPlanIds.fulfilled, (state, action) => {
        state.comparePlans.isLoading = false
        state.comparePlans.data = action.payload || []
        state.comparePlans.error = null
      })
      .addCase(getShellPlansByCommunityPlanIds.rejected, (state, action) => {
        state.comparePlans.isLoading = false
        state.comparePlans.error =
          action.error.message ??
          'Error fetching shell plans by community plan ids'
      })
      .addCase(getUnitPlansByCommunityPlanIds.pending, (state) => {
        state.comparePlans.isLoading = true
        state.comparePlans.error = null
      })
      .addCase(getUnitPlansByCommunityPlanIds.fulfilled, (state, action) => {
        state.comparePlans.isLoading = false
        state.comparePlans.data = action.payload || []
        state.comparePlans.error = null
      })
      .addCase(getUnitPlansByCommunityPlanIds.rejected, (state, action) => {
        state.comparePlans.isLoading = false
        state.comparePlans.error =
          action.error.message ??
          'Error fetching unit plans by community plan ids'
      })
      .addCase(getSharedPlans.pending, (state) => {
        state.sharedPlans.isLoading = true
        state.sharedPlans.error = null
      })
      .addCase(getSharedPlans.fulfilled, (state, action) => {
        state.sharedPlans.isLoading = false
        state.sharedPlans.data = action.payload
        state.sharedPlans.error = null
      })
      .addCase(getSharedPlans.rejected, (state, action) => {
        state.sharedPlans.isLoading = false
        state.sharedPlans.error =
          action.error.message ?? 'Error in sharing the plans'
      })
      .addCase(deletePlan.pending, (state) => {
        state.deletePlan.isLoading = true
        state.deletePlan.error = null
      })
      .addCase(deletePlan.fulfilled, (state, action) => {
        const planIdToRemove = action.payload.planId
        state.deletePlan.isLoading = false
        // Plain Immer filter, like the draft branch below — the previous
        // structuredClone deep-copied the entire plans array to drop one row.
        state.allPlans.data = state.allPlans.data.filter(
          (plan: IModifiedPlan) =>
            plan.id?.toUpperCase() !== planIdToRemove?.toUpperCase()
        )

        // Also remove from draft plans
        state.allDraftPlans.data = state.allDraftPlans.data.filter(
          (plan: IModifiedPlan) =>
            plan.id?.toUpperCase() !== planIdToRemove?.toUpperCase()
        )

        state.deletePlan.error = null
      })
      .addCase(deletePlan.rejected, (state, action) => {
        state.deletePlan.isLoading = false
        state.deletePlan.error =
          action.error.message ?? 'Error in deleting the plan'
      })
      .addCase(propagateChanges.pending, (state) => {
        state.propagateChanges.isLoading = true
        state.propagateChanges.error = null
      })
      .addCase(propagateChanges.fulfilled, (state) => {
        state.propagateChanges.isLoading = false
        state.propagateChanges.error = null
      })
      .addCase(propagateChanges.rejected, (state, action) => {
        state.propagateChanges.isLoading = false
        state.propagateChanges.error =
          action.payload?.message ??
          action.error.message ??
          'Error propagating changes'
      })
      .addCase(sendCopyRequest.pending, (state) => {
        state.planCopyRequest.isLoading = true
        state.deletePlan.error = null
      })
      .addCase(sendCopyRequest.fulfilled, (state, action) => {
        state.planCopyRequest.isLoading = false

        if (!state.planCopyRequest.data) {
          state.planCopyRequest.data = {
            items: [],
            nextContinuationToken: '',
          }
        }

        state.planCopyRequest.data.items = [
          ...state.planCopyRequest.data.items,
          action.payload as PlanCopyRequestItem,
        ]

        state.planCopyRequest.data.nextContinuationToken = (
          action.payload as PlanCopyRequestResponse
        ).nextContinuationToken

        state.approvals.data = [
          ...state.approvals.data,
          action.payload as ApprovalData,
        ]

        state.planCopyRequest.error = null
      })
      .addCase(sendCopyRequest.rejected, (state, action) => {
        state.planCopyRequest.isLoading = false
        state.planCopyRequest.error =
          action.payload?.message ??
          action.error.message ??
          COPY_REQUEST_ERROR_MESSAGE
      })
      .addCase(getRequestedApprovals.pending, (state) => {
        state.approvals.isLoading = true
        state.approvals.error = null
      })
      .addCase(getRequestedApprovals.fulfilled, (state, action) => {
        state.approvals.isLoading = false
        state.approvals.data = action.payload?.items
        state.approvals.approverRegions = action.payload?.approverRegions
        state.approvals.error = null
      })
      .addCase(getRequestedApprovals.rejected, (state, action) => {
        state.approvals.isLoading = false
        state.approvals.error =
          action.error.message ?? 'Error in getting approvals'
      })
      .addCase(getIsApprover.fulfilled, (state, action) => {
        state.isCurrentUserAnApprover = action.payload?.isApprover
        state.isCurrentUserARegionalApprover =
          action.payload?.isRegionalApprover ?? false
        state.regionalPlanApproverRegions =
          action.payload?.regionalPlanApproverRegions ?? []
      })
      .addCase(requestRegionalPlanApproval.pending, (state) => {
        state.regionalApprovals.isLoading = true
        state.regionalApprovals.error = null
      })
      .addCase(requestRegionalPlanApproval.fulfilled, (state, action) => {
        state.regionalApprovals.isLoading = false
        state.regionalApprovals.data = upsertRegionalApproval(
          state.regionalApprovals.data,
          action.payload
        )
        // The requester's own list is what drives the three-state Submit button,
        // so a freshly requested approval has to land there too or the button
        // never leaves "Submit for approval".
        state.myRegionalApprovals.data = upsertRegionalApproval(
          state.myRegionalApprovals.data,
          action.payload
        )
      })
      .addCase(requestRegionalPlanApproval.rejected, (state, action) => {
        state.regionalApprovals.isLoading = false
        state.regionalApprovals.error =
          action.error.message ?? 'Error requesting regional plan approval'
      })
      .addCase(requestNewRegionalPlanElevation.fulfilled, (state, action) => {
        // The requester's own list seeds the notification listener, so the new
        // request has to land there for the fulfilment toast to fire later in
        // this session.
        state.myRegionalApprovals.data = upsertRegionalApproval(
          state.myRegionalApprovals.data,
          action.payload
        )
      })
      .addCase(getRegionalPlanApprovals.pending, (state, action) => {
        const target =
          action.meta.arg?.view === 'queue'
            ? state.regionalApprovals
            : state.myRegionalApprovals
        target.isLoading = true
        target.error = null
      })
      .addCase(getRegionalPlanApprovals.fulfilled, (state, action) => {
        const target =
          action.meta.arg?.view === 'queue'
            ? state.regionalApprovals
            : state.myRegionalApprovals
        target.isLoading = false
        target.data = action.payload?.items ?? []
        if (action.payload?.regionalPlanApproverRegions) {
          state.regionalPlanApproverRegions =
            action.payload.regionalPlanApproverRegions
        }
      })
      .addCase(getRegionalPlanApprovals.rejected, (state, action) => {
        const target =
          action.meta.arg?.view === 'queue'
            ? state.regionalApprovals
            : state.myRegionalApprovals
        target.isLoading = false
        target.error =
          action.error.message ?? 'Error fetching regional plan approvals'
      })
      .addCase(decideRegionalPlanApproval.fulfilled, (state, action) => {
        state.regionalApprovals.data = upsertRegionalApproval(
          state.regionalApprovals.data,
          action.payload
        )
        state.myRegionalApprovals.data = upsertRegionalApproval(
          state.myRegionalApprovals.data,
          action.payload
        )
      })
      .addCase(decideRegionalPlanApproval.rejected, (state, action) => {
        state.regionalApprovals.error =
          action.error.message ?? 'Error deciding regional plan approval'
      })
      .addCase(relinkRegionalPlanApproval.fulfilled, (state, action) => {
        state.regionalApprovals.data = upsertRegionalApproval(
          state.regionalApprovals.data,
          action.payload
        )
        state.myRegionalApprovals.data = upsertRegionalApproval(
          state.myRegionalApprovals.data,
          action.payload
        )
      })
      .addCase(deleteRegionalPlanApproval.fulfilled, (state, action) => {
        state.regionalApprovals.data = state.regionalApprovals.data.filter(
          (approval) => approval.id !== action.payload.id
        )
        state.myRegionalApprovals.data = state.myRegionalApprovals.data.filter(
          (approval) => approval.id !== action.payload.id
        )
      })
      .addCase(updateApprovalStatus.pending, (state) => {
        state.approvals.isLoading = true
        state.approvals.error = null
      })
      .addCase(updateApprovalStatus.fulfilled, (state, action) => {
        state.approvals.isLoading = false
        const approvalsData: ApprovalData[] = JSON.parse(
          JSON.stringify(state.approvals.data)
        )
        const planIdAndUserId = action.payload.id + action.payload.userId
        const isPlanInArray = approvalsData.some(
          (item) => item.id + item.userId === planIdAndUserId
        )
        const updatedApprovalsData = isPlanInArray
          ? approvalsData
              .filter((item) => item.id + item.userId !== planIdAndUserId)
              .concat(action.payload)
          : [...approvalsData, action.payload]

        state.approvals.data = updatedApprovalsData
        state.approvals.error = null
      })
      .addCase(updateApprovalStatus.rejected, (state, action) => {
        state.approvals.isLoading = false
        state.approvals.error =
          action.error.message ?? 'Error in getting approvals'
      })
      .addCase(updatePlanAttributes.fulfilled, (state, action) => {
        const changedSinglePlan = state.allPlans.data.find(
          (plan) => plan.id === action.payload.planId?.toUpperCase()
        )
        // Check if the hidden property has changed
        if (
          changedSinglePlan &&
          changedSinglePlan?.hidden !== action.payload.hidden
        ) {
          changedSinglePlan.hidden = action.payload.hidden
        }
      })
      .addCase(getModsySpecs.fulfilled, (state, action) => {
        const communityId = action.meta.arg
        const spec = action.payload as { name?: string; url?: string }
        if (!spec) return
        const modsySpec = { label: spec.name ?? '', link: spec.url ?? '' }
        state.comparePlans.data = state.comparePlans.data.map((plan) => {
          if (
            plan.planOverview?.communityId === communityId &&
            plan.planOverview?.planCategory === planCategories.COMMUNITY
          ) {
            return {
              ...plan,
              specifications: {
                ...plan.specifications,
                modsy: [modsySpec],
              },
            }
          }
          return plan
        })
      })
  },
  initialState,
  name: 'plans',
  reducers: {
    appendPlansData: (state, action) => {
      // Drop pages from a superseded fan-out so an aborted refresh cannot append
      // stale data on top of the current run and create duplicates.
      if (
        isStaleAllPlansRequest(
          state.allPlans.currentRequestId,
          action.payload.requestId
        )
      ) {
        return
      }
      state.allPlans.data = state.allPlans.data.concat(action.payload.data)
    },
    patchPlanData: (state, action) => {
      state.allPlans.data = state.allPlans.data.map((plan) =>
        plan.id === action.payload.planId?.toUpperCase()
          ? { ...plan, _etag: action.payload._etag }
          : plan
      )
    },
    removePlanFromDraftPlans: (state, action) => {
      const planIdToRemove = action.payload.planId
      state.allDraftPlans.data = state.allDraftPlans.data.filter(
        (plan) => plan.id?.toUpperCase() !== planIdToRemove?.toUpperCase()
      )
    },
    resetAllPlansData: (state) => {
      state.allPlans.data = []
      state.allPlans.isLoading = false
      state.allPlans.error = null
      state.allPlans.continuationToken = null
      state.allPlans.fullyLoaded = false
      state.allPlans.totalRecords = null
    },
    resetDraftPlansData: (state) => {
      state.allDraftPlans.data = []
      state.allDraftPlans.isLoading = false
      state.allDraftPlans.error = null
      state.allDraftPlans.continuationToken = null
      state.allDraftPlans.fullyLoaded = false
      state.allDraftPlans.totalRecords = null
    },
    setSingleFamilyDraftPlanDataUpToDate: (state, action) => {
      state.isSingleFamilyDraftPlanDataUpToDate = action.payload
    },
    setSingleFamilySubmittedPlanDataUpToDate: (state, action) => {
      state.isSingleFamilySubmittedPlanDataUpToDate = action.payload
    },
  },
})

export const {
  appendPlansData,
  patchPlanData,
  removePlanFromDraftPlans,
  resetAllPlansData,
  resetDraftPlansData,
  setSingleFamilyDraftPlanDataUpToDate,
  setSingleFamilySubmittedPlanDataUpToDate,
} = plans.actions
